import { z } from 'zod'
import { CompanyIdSchema, idSchemaV2, isId, TeamIdSchema, type UserId } from '../ids'
import { type AuditProps, AuditPropsSchema } from '../common'
import { ArchivableSchema } from '../archive'

export const TERROS_ADMIN_ROLE_ID: RoleId = 'Role.atos8lzt'

export const RoleIdSchema = idSchemaV2('Role')
export type RoleId = z.infer<typeof RoleIdSchema>
export const isRoleId = isId(RoleIdSchema)

export type WithRoleId = {
  roleId: RoleId
}
export const RoleTypeSchema = z.union([z.enum(['admin', 'manager', 'recruit']), z.null()])
export type RoleType = z.infer<typeof RoleTypeSchema>

export const RoleAssignmentSchema = z.enum(['user', 'team'])
export type RoleAssignment = z.infer<typeof RoleAssignmentSchema>

export const UnsavedRoleSchema = z.object({
  name: z.string().trim().min(1, 'Role name cannot be empty'),
  roleType: RoleTypeSchema.optional(),
  description: z.string().optional(),
  assignment: RoleAssignmentSchema.optional(),
})
export type UnsavedRole = z.infer<typeof UnsavedRoleSchema>

export const RoleDataSchema = z.object({
  ...ArchivableSchema.shape,
  ...AuditPropsSchema.shape,
  ...UnsavedRoleSchema.shape,
  roleId: RoleIdSchema,
  companyId: CompanyIdSchema,
  canAssign: z.boolean().optional().describe('Whether the current user can assign this role'),
})
export type RoleData = z.infer<typeof RoleDataSchema>

export type RoleMemberData = AuditProps & {
  roleId: RoleId
  userId: UserId
}

export const TinyRoleSchema = RoleDataSchema.pick({
  roleId: true,
  roleType: true,
  name: true,
})
export type TinyRole = z.infer<typeof TinyRoleSchema>

export const MicroRoleSchema = RoleDataSchema.pick({ roleId: true, roleType: true })
export type MicroRole = z.infer<typeof MicroRoleSchema>

export const MicroRoleMemberSchema = z.object({ roleId: RoleIdSchema })
export type MicroRoleMember = z.infer<typeof MicroRoleMemberSchema>

export type PartialRoleData = Partial<RoleData> & WithRoleId

export const UserRoleAssignmentSchema = z
  .object({
    ...TinyRoleSchema.shape,
    teamId: TeamIdSchema.optional().meta({ description: 'Team the role is applied on; omitted for a direct role' }),
    teamName: z.string().optional(),
  })
  .meta({ id: 'userRoleAssignment', description: 'One role a user holds, either directly or on one team' })
export type UserRoleAssignment = z.infer<typeof UserRoleAssignmentSchema>
